/**
 * Lectures dérivées du moteur, regroupées.
 *
 * Ce fichier ne calcule **rien** : il assemble les arguments que les helpers de
 * `@maxime/shared` attendent (le moteur est pur, on lui passe le contenu — arbitrage A25) et
 * il les rend agréables à appeler depuis un composant. Toute la logique reste dans le moteur ;
 * si une question n'a pas de réponse ici, c'est qu'il faut l'ajouter au moteur, pas la
 * bricoler dans un écran.
 */

import {
  assistsDuMiniJeu,
  collectModifiers,
  hasCapability,
  managerActif,
  producteurActif,
  skillsDuContenu,
  type ActiveModifiers,
  type GameState,
} from "@maxime/shared";
import type { CapabilityId, MinigameId } from "@maxime/shared";

import { contenu } from "./content";

/** Les modificateurs actifs (skills possédés + effets portés par l'état). */
export function modificateurs(state: GameState): ActiveModifiers {
  return collectModifiers(state, skillsDuContenu(contenu()));
}

/** Une capacité est-elle débloquée par l'arbre ? (`unlock`, SPEC-CONTENU §2.3) */
export function capacite(state: GameState, capability: CapabilityId): boolean {
  return hasCapability(modificateurs(state), capability);
}

/**
 * ⟦C8 · A-120⟧ Le manager est-il RÉELLEMENT en poste ? (interrupteur, nœud possédé, pas
 * congédié, producteur actif — `manager.ts::managerActif`). C'est la garde que le minuteur
 * client de `EvenementModal` lit avant de démarrer : sans manager en poste, le réglage n'a
 * rien à faire, un événement bloque comme aujourd'hui ⟦A-118 §1⟧.
 */
export function managerEnPoste(state: GameState): boolean {
  const mods = modificateurs(state);
  return managerActif(state, contenu(), mods, producteurActif(state, mods));
}

/** Les assists d'un mini-jeu apportés par les nœuds possédés (SPEC-CONTENU §2.4). */
export function assists(state: GameState, minigame: MinigameId): Record<string, number> {
  return assistsDuMiniJeu(state, skillsDuContenu(contenu()), minigame);
}
